import { useEffect, useState } from "react";
import {
    faCamera,
    faDesktop,
    faHandPointer,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ConnectionData from "../../js/connection/ConnectionData";
import ChannelConnection from "../../js/connection/ChannelConnection";
import DrawingOverlay from "./overlay/DrawingOverlay";
import { DrawingMode } from "./overlay/DrawingControls";

type Props = {
    connection: ChannelConnection;
    connectionData: ConnectionData;
    onScreenshotClick: (connectionData: ConnectionData) => void;
    onUploadClick: (connectionData: ConnectionData) => void;
};

export default function ClientOverlay({
    connection,
    connectionData,
    onScreenshotClick,
    onUploadClick,
}: Props) {
    const [isFocused, setFocused] = useState<boolean>(false);
    const [drawingMode, setDrawingMode] = useState<DrawingMode>("none");
    const [strokeColor, setStrokeColor] = useState<string>("#ef4444");

    useEffect(() => {
        function onViewFocus(data: ConnectionData | null) {
            if (data?.id === connectionData.id) {
                setFocused(true);
            } else if (isFocused) {
                setDrawingMode("none");
                setFocused(false);
            }
        }

        function onFocusMode({
            id,
            mode,
        }: {
            id: string | null;
            mode: "annotate" | "control" | null;
        }) {
            if (mode === null) {
                setDrawingMode("none");
                return;
            }
            if (id !== connectionData.id) return;
            setDrawingMode(mode === "annotate" ? "pen" : "none");
        }

        function onDrawingMode({ id, mode }: { id: string; mode: DrawingMode }) {
            if (id === connectionData.id) setDrawingMode(mode);
        }

        function onDrawingColor({ id, color }: { id: string; color: string }) {
            if (id === connectionData.id) setStrokeColor(color);
        }

        connection.events.addListener("onviewfocus", onViewFocus);
        connection.events.addListener("onfocusmode", onFocusMode);
        connection.events.addListener("ondrawingmode", onDrawingMode);
        connection.events.addListener("ondrawingcolor", onDrawingColor);
        return () => {
            connection.events.removeListener("onviewfocus", onViewFocus);
            connection.events.removeListener("onfocusmode", onFocusMode);
            connection.events.removeListener("ondrawingmode", onDrawingMode);
            connection.events.removeListener("ondrawingcolor", onDrawingColor);
        };
    }, [connection.events, connectionData.id, isFocused]);

    // Let annotation canvas receive clicks above hardware video while pinned
    useEffect(() => {
        const video = connectionData.view?.querySelector("video") as HTMLElement | null;
        if (!video) return;
        video.style.pointerEvents = isFocused ? "none" : "";
        return () => {
            video.style.pointerEvents = "";
        };
    }, [isFocused, connectionData.view]);

    function isHoloLens() {
        return connectionData.userAlias && connectionData.userAlias.includes("Unity_Hololens");
    }

    function isScreensharing() {
        return connectionData.roles && connectionData.roles.includes("screenshare");
    }

    const isRemoteScreenshare = isScreensharing() && !connectionData.isLocal;

    return (
        <>
            {/* Name label */}
            <div className="co-name-label">
                {isScreensharing() && (
                    <FontAwesomeIcon icon={faDesktop} className="co-name-icon" />
                )}
                <span>{connectionData.userAlias}</span>
                {connectionData.isLocal && (
                    <span className="co-you-tag">You</span>
                )}
            </div>

            {/* Top action buttons */}
            {(isRemoteScreenshare || isHoloLens()) && (
                <div className="co-top-actions">
                    {isRemoteScreenshare && (
                        <button
                            className="co-action-btn"
                            title="Request remote control"
                            onClick={() => connection.messager.sendControlRequest(connectionData)}
                        >
                            <FontAwesomeIcon icon={faHandPointer} />
                            <span>Control</span>
                        </button>
                    )}
                    {isHoloLens() && (
                        <button
                            className="co-action-btn"
                            title="Capture frame"
                            onClick={() => onScreenshotClick(connectionData)}
                        >
                            <FontAwesomeIcon icon={faCamera} />
                            <span>Capture</span>
                        </button>
                    )}
                </div>
            )}

            {/* Drawing overlay (canvas) */}
            <div className="co-drawing-layer">
                <DrawingOverlay
                    drawingMode={drawingMode}
                    strokeColor={strokeColor}
                    canvasDrawing={connection.sfuConnection.canvasDrawing}
                    connectionData={connectionData}
                />
            </div>

            <style>{`
                .co-drawing-layer {
                    position: absolute;
                    inset: 0;
                    z-index: 10;
                    pointer-events: none;
                }

                .co-name-label {
                    position: absolute;
                    bottom: 12px;
                    left: 12px;
                    z-index: 30;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(8, 10, 16, 0.72);
                    backdrop-filter: blur(10px);
                    -webkit-backdrop-filter: blur(10px);
                    color: white;
                    font-size: 0.78rem;
                    font-weight: 550;
                    padding: 5px 10px;
                    border-radius: 9px;
                    max-width: calc(100% - 64px);
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    border: 1px solid rgba(255, 255, 255, 0.12);
                    box-shadow: 0 4px 14px rgba(0,0,0,0.25);
                    pointer-events: none;
                }

                .co-name-icon {
                    color: #93c5fd;
                    font-size: 0.72rem;
                    flex-shrink: 0;
                }

                .co-you-tag {
                    font-size: 0.62rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.05em;
                    color: #93c5fd;
                    background: rgba(59, 130, 246, 0.2);
                    border: 1px solid rgba(59, 130, 246, 0.35);
                    border-radius: 5px;
                    padding: 1px 5px;
                    flex-shrink: 0;
                }

                .co-top-actions {
                    position: absolute;
                    top: 12px;
                    left: 12px;
                    z-index: 40;
                    display: flex;
                    gap: 6px;
                    pointer-events: auto;
                }

                .co-action-btn {
                    display: flex;
                    align-items: center;
                    gap: 5px;
                    background: rgba(8, 10, 16, 0.72);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.14);
                    border-radius: 9px;
                    color: white;
                    font-size: 0.76rem;
                    font-weight: 550;
                    padding: 6px 11px;
                    cursor: pointer;
                    transition: background 0.15s, border-color 0.15s;
                    font-family: inherit;
                }

                .co-action-btn:hover {
                    background: rgba(59, 130, 246, 0.75);
                    border-color: transparent;
                }
            `}</style>
        </>
    );
}
